import React, { useState } from 'react';
import AuthFormContainer from '../../components//containers/AuthFormContainer';
import CloseButton from '../../components/custom-components/buttons/CloseButton';
import { PATHS } from '../../router/routes';
import { Text, Box, Stack, useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { passwordSchema } from '../../utils/schemas';
import ZaraButton from '../../components/custom-components/buttons/Button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { ChangePass } from '../../graphql/mutations/ChangePass';
import Dashboard from '../../components/Dashboard';
import AdaptiveContainer from '../../components/containers/AdaptiveContainer';
import MainContainer from '../../components/containers/MainContainer';
import { useError } from '../../error-provider/ErrorProvider';
import FormInput from '../../components/custom-components/Inputs/FormInput';
import { INPUT_PROPS } from '../../utils/constants';

const schema = z
    .object({
        newPassword: passwordSchema,
        confirmPassword: passwordSchema,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[A-Z])/.test(data.newPassword), {
        message: 'Password does not have uppercase letter.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[a-z])/.test(data.newPassword), {
        message: 'Password does not have lowercase letter.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*\d)/.test(data.newPassword), {
        message: 'Password does not have digit.',
        path: ['confirmPassword'],
    })
    .refine((data) => /(?=.*[!@#$%^&*])/.test(data.newPassword), {
        message: 'Password does not have special character.',
        path: ['confirmPassword'],
    });

const ChangePassword = () => {
    const toast = useToast();
    const router = useNavigate();
    const { showError } = useError();
    const { control, handleSubmit } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const [onChangePassword, { loading }] = useMutation(ChangePass, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [hideNewPass, setHideNewPass] = useState(true);
    const [hideConfirmPass, setHideConfirmPass] = useState(true);

    async function onSubmit(data) {
        const variables = {
            email: localStorage.getItem('email'),
            code: localStorage.getItem('code') ?? 0,
            password1: data.newPassword,
            password2: data.confirmPassword,
        };

        await onChangePassword({
            variables: variables,
            onCompleted(data) {
                if (data.changePassword.__typename === 'ErrorType') {
                    toast({
                        status: 'error',
                        title: data.changePassword.message,
                        isClosable: true,
                    });
                }
                if (data.changePassword.__typename === 'SuccessType') {
                    toast({
                        status: 'success',
                        title: data.changePassword.message,
                        isClosable: true,
                    });
                    router(PATHS.SIGN_IN);
                }
            },
        });
    }

    return (
        <Dashboard title={PATHS.CHANGE_PASSWORD}>
            <MainContainer bg='#e8e8e8'>
                <AdaptiveContainer title={PATHS.CHANGE_PASSWORD}>
                    <AuthFormContainer
                        bg='white'
                        pos='relative'
                        w='100%'
                        borderRadius='6px'
                        p='20px 0px'
                        boxShadow='lg'
                    >
                        <CloseButton isSignIn='true' path={PATHS.SIGN_IN} />
                        <Box p='16px 24px'>
                            <Text
                                fontWeight='700'
                                color='#2D3748'
                                fontSize={{ base: '20px', lg: '36px' }}
                                lineHeight='43.2px'
                            >
                                Change Password
                            </Text>
                        </Box>
                        <Box as='form' onSubmit={handleSubmit(onSubmit)}>
                            <Stack justifyContent='center' alignItems='center'>
                                <Stack spacing='3' w='100%' p='0px 24px'>
                                    <FormInput
                                        variant='password'
                                        label={INPUT_PROPS.PASSWORD.LABEL}
                                        control={control}
                                        controllName={INPUT_PROPS.NEW_PASSWORD.CONTROLL_NAME}
                                        hidePass={hideNewPass}
                                        setHidePass={setHideNewPass}
                                        placeholder={INPUT_PROPS.PASSWORD.PLACEHOLDER}
                                    />
                                    <FormInput
                                        variant='password'
                                        label={INPUT_PROPS.CONFIRM_PASSWORD.LABEL}
                                        control={control}
                                        controllName={INPUT_PROPS.CONFIRM_PASSWORD.CONTROLL_NAME}
                                        hidePass={hideConfirmPass}
                                        setHidePass={setHideConfirmPass}
                                        placeholder={INPUT_PROPS.CONFIRM_PASSWORD.PLACEHOLDER}
                                    />
                                </Stack>
                                <Box w='100%' h='72px' p='16px 24px'>
                                    <ZaraButton isLoading={loading} isDisabled={loading} type='submit' w='100%'>
                                        Confirm Password
                                    </ZaraButton>
                                </Box>
                            </Stack>
                        </Box>
                    </AuthFormContainer>
                </AdaptiveContainer>
            </MainContainer>
        </Dashboard>
    );
};

export default ChangePassword;
